/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Ha Van Truong (Vincent) (hvtruong3@rsupartners.com)
 *
 * @description
 *
 * @created at Web Jan 08 2025
 **/

import { useCallback, useEffect, useState } from "react";
import { Avatar, Column, Icon, Loader, Row, Typography } from "@/frameworks/designStuff/components";
import { Drawer } from "@/frameworks/designStuff/compounds";
import { Maybe } from "@/frameworks/libraries/components";
import { Utility, ChronoUtils } from "@/frameworks/libraries/utilities";
import styled from "@emotion/styled";

import { _T, _P } from "@rfice/language";
import { MessageList } from "@/frameworks/core/chat";
import { ChatRenderer, VoteRenderer } from "@/frameworks/core/components";
import { useIntersectionObserver } from "@/frameworks/libraries";
import { useChatDataStore } from "@/modules/chat/store";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useChat } from "@/frameworks/core/API";

export default function MessageSearchResult({
	onComplete,
	searchText,
	roomID
}: {
	onComplete: () => void;
	searchText: string;
	roomID: string;
}) {
	const chatAPI = useChat();
	const [searchTextValue, setSearchTextValue] = useState("");
	const setSearchTextSelection = useChatDataStore((state) => state.set_chat_search_selected);
	const handleClickConversation = ({ searchChatTextId }: { searchChatTextId: string }) => {
		setSearchTextSelection(searchChatTextId);
		onComplete();
	};

	const { data: pagedConversations, fetchNextPage: fetchNextConversation } = useInfiniteQuery({
		queryKey: ["searchOnConversation", searchTextValue, roomID],
		queryFn: async ({ pageParam = undefined }) => {
			const requestParam: WebServices.Chat.Search.Request = {
				keyword: searchTextValue || "",
				pageSize: 40,
				cursorId: pageParam,
				roomId: roomID,
				searchTarget: "ROOM"
			};
			const result = (await chatAPI.searchMessages.execute(requestParam)).data;
			return {
				...result,
				nextPage: result?.content[result.content.length - 1].roomMessageId,
				hasNext: result?.hasNext ?? false
			};
		},
		refetchOnWindowFocus: false,
		getNextPageParam: (lastPage) => lastPage.nextPage,
		cacheTime: 0,
		staleTime: 0,
		refetchOnMount: true,
		enabled: searchTextValue.trim().length > 0 && typeof searchTextValue !== "undefined"
	});

	const totalConversationCount = pagedConversations?.pages[0]?.totalCount || 0;

	const conversations =
		pagedConversations?.pages
			.map((el) => el?.content || [])
			.flat()
			?.filter((el) => el.roomId == roomID) || [];

	useEffect(() => {
		setSearchTextValue(searchText);
	}, [searchText]);

	const onIntersecting = useCallback(() => {
		const pageSize = pagedConversations?.pages.length || 0;
		if (pagedConversations?.pages[pageSize - 1]?.hasNext) {
			fetchNextConversation();
		}
	}, [totalConversationCount]);

	const { observerRef } = useIntersectionObserver(
		({ isIntersecting }) => {
			if (isIntersecting) {
				onIntersecting();
			}
		},
		[onIntersecting]
	);

	if (!conversations) {
		return (
			<Drawer.Body css={{ width: "100%", height: "100%" }}>
				<Column verticalAlign="center" horizonAlign="center" css={{ width: "100%", height: "100%" }}>
					<Loader />
				</Column>
			</Drawer.Body>
		);
	}

	return (
		<Drawer.Body css={{ width: "100%", height: "100%", overflow: "auto" }}>
			<TotalCountWrapper id="total-count" gap={8}>
				<Typography.p typoSize="Body4">
					{_T("검색결과 ({{count}}건)", {
						count: totalConversationCount < 100 ? totalConversationCount : "99+"
					})}
				</Typography.p>
			</TotalCountWrapper>

			<Maybe
				test={conversations.length > 0}
				fallback={
					<Column verticalAlign="center" horizonAlign="center" css={{ width: "100%", height: "inherit" }}>
						<Icon name="ToastError24" fill="#000" size={52} />
						<Typography typoSize="Body3" typoColor="neutral_70_100" align="center">
							{_P("검색 결과가 없습니다.<br />다른 검색어를 입력해주세요.")}
						</Typography>
					</Column>
				}
			>
				<Column id="content-wrapper" gap={8} css={{ padding: "8px 16px", paddingTop: "30px" }}>
					{conversations?.map((conversation, index) => {
						if (!Utility.json.isJson(conversation.content)) return;
						const emphasisText = `<span id='search-text'>${searchText}</span>`;

						const vote = {
							...conversation.vote,
							title: conversation.vote?.title.replace(searchText, emphasisText)
						} as MessageList[string]["vote"];

						const content =
							conversation.messageType === "VOTE"
								? (JSON.parse(conversation.content) as ChatServices.Format2.VoteMessage)
								: (JSON.parse(
										conversation.content.replace(searchText, emphasisText)
									) as ChatServices.Format2.Message);

						if (!content.elements) return;

						const targetIndexOfRichtext = content.elements.findIndex((v) => {
							if (v.type === "text") {
								return typeof v.text === "string" && v.text.includes(emphasisText);
							}
							if (v.type === "user") {
								return v.name.includes(emphasisText);
							}
							if (v.type === "codeBlock") {
								return v.code.includes(emphasisText);
							}
							if (v.type === "emoticon") {
								return v.text.includes(emphasisText);
							}
							if (v.type === "file") {
								return v.title.includes(emphasisText);
							}
							if (v.type === "image") {
								return v.title?.includes(emphasisText);
							}
							if (v.type === "link") {
								return v.title.includes(emphasisText);
							}
						});

						return (
							<SearchedConversationItem
								id="searched-item"
								key={conversation.roomMessageId}
								gap={12}
								ref={index === conversations.length - 5 ? observerRef : null}
								onClick={() => {
									handleClickConversation({
										searchChatTextId: conversation.roomMessageId
									});
								}}
							>
								<Avatar
									kind="people_32"
									type="image"
									src={conversation.user?.profileImagePath}
									text={String(conversation.user?.name)}
									status={(() => {
										switch (conversation.user?.activeStatus) {
											case "ACTIVE":
												return "Green";
											case "OFFLINE":
												return "Gray";
											case "FOCUS":
												return "Red";
											case "AWAY":
												return "Yellow";
											default:
												return undefined;
										}
									})()}
								/>
								<ContentWrapper>
									<Row
										gap={8}
										verticalAlign="center"
										style={{ display: "flex", width: "100%", justifyContent: "space-between" }}
									>
										<Typography typoSize="Title1" typoColor="neutral_80_100">
											{conversation.user?.name}
										</Typography>
										<Typography typoSize="Body4" typoColor="neutral_60_100">
											{ChronoUtils.formatTimeChatTime(conversation.timestamp)}
										</Typography>
									</Row>
									<EmphasisSearchText tag="div">
										<Maybe
											test={content.type === "richtext"}
											fallback={
												<VoteRenderer
													chatForm={content as ChatServices.Format2.VoteMessage}
													vote={vote}
													roomID={conversation.roomId}
													spaceID={conversation.spaceId}
													voteType={content.elements?.[0]?.key}
													isSearch={true}
												/>
											}
										>
											<ChatRenderer
												chatForm={content as ChatServices.Format2.Message}
												startLine={targetIndexOfRichtext > 3 ? targetIndexOfRichtext - 3 : 0}
												maxLine={targetIndexOfRichtext + 3}
											/>
										</Maybe>
									</EmphasisSearchText>
								</ContentWrapper>
							</SearchedConversationItem>
						);
					})}
				</Column>
			</Maybe>
		</Drawer.Body>
	);
}

export const SearchedConversationItem = styled(Row)`
	width: 100%;
	padding: 8px 4px;
	border-radius: 16px;
	&:hover {
		background-color: ${({ theme }) => theme.colors.secondary_80_8};
	}
`;

export const ContentWrapper = styled(Column)`
	width: -webkit-fill-available;
	pointer-events: none;
`;

export const EmphasisSearchText = styled(Typography)`
	width: 100%;
	#search-text {
		background-color: ${({ theme }) => theme.colors.accent_10_100};
	}
`;

export const TotalCountWrapper = styled(Row)`
	position: absolute;
	z-index: 1000;
	width: 100%;
	display: flex;
	align-items: center;
	padding: 0 4px 0 20px;
	background-color: ${({ theme }) => theme.colors.accent_10_100};
`;
